/* eslint-disable jsx-a11y/media-has-caption */
import React from 'react';
import { connect } from 'react-redux';
import videojs from 'video.js';
import { Slider, LinearProgress } from '@mui/material';
// @ts-ignore
import SetInterval from 'set-interval';
import _ from 'lodash';
import { clipsPreviewVideoJsOptions } from './clipsPreviewVideoJsOptions';
import {
  IProps,
  IState,
  IStateProps,
  IDispatchProps,
} from './interfaces';
import { IAppState } from '../../../state/reducers';
import {
  selectSelectedClipsWithPrePostRolls,
  selectClipStartingPoints,
  selectTotalClipsDuration,
  selectMarksForPublishPreviewPlayer,
  selectIsPrePostRollLoading,
  setThumbnailFrame,
  selectThumbnailFrame,
} from '../../../state/selectedClipsPreview';
import VaultButton from '../VaultButton';
import {
  Container,
  PlayerContainer,
  ProgressContainer,
  Controls,
  SliderContainer,
  AddPreRollButton,
  PlayPauseButton,
} from './styles';
import { oneFrameInSec, oneFrameInMs } from '../../VirtualClipper/utils/videojs';
import { openPrePostRollSelection, ROLL } from '../../VirtualClipper/state/prePostRollSelection';
import ThumbnailSelection from './ThumbnailSelection/component';
import PrePostRollDialog from '../PrePostRollDialog/component';
import { selectSource } from '../../VirtualClipper/state/clipperMediaContent';

// Helper to safely call play() and catch autoplay policy errors
const safePlay = (player: videojs.Player) => {
  const playPromise = player.play();
  if (playPromise !== undefined) {
    playPromise.catch(() => {
      // Autoplay was prevented - this is expected behavior
    });
  }
};

class ClipsPreviewPlayer extends React.Component<IProps, IState> {
  private player?: videojs.Player;

  state: IState = {
    isReady: false,
    isPlaying: false,
    isSliderDragged: false,
    currentClipIndex: 0,
    currentPosition: 0,
  };

  componentDidUpdate(prevProps: IProps) {
    this.resetPlayerOnPrePostRollChange(prevProps);
  }

  componentDidMount() {
    this.player = videojs(clipsPreviewVideoJsOptions.id, clipsPreviewVideoJsOptions);
    this.setPlayerToFirstClip();
    this.setState({
      isReady: true,
    });
  }

  componentWillUnmount() {
    if (this.player) {
      SetInterval.clear('updatePreviewPlayer');
      this.player.dispose();
    }
  }

  setPlayerToFirstClip() {
    const { clipList } = this.props;
    const firstClip = clipList[0];
    if (this.player.src() !== firstClip.url && typeof firstClip.url === 'string') {
      const extension = firstClip.url.split('.').pop();
      this.player.src({
        src: firstClip.url,
        type: extension === 'm3u8' ? 'application/x-mpegURL' : 'video/mp4',
      });
    }
    this.player.ready(() => {
      this.player.currentTime(firstClip.markInTime);
    });
    this.setState({
      currentPosition: 0,
      currentClipIndex: 0,
    });
  }

  resetPlayerOnPrePostRollChange = (prevProps: IProps) => {
    const { clipList } = this.props;
    const { clipList: prevClipList } = prevProps;

    if (!clipList.length) {
      return;
    }

    if (clipList.length !== prevClipList.length
      || clipList[0].url !== prevClipList[0].url
      || clipList[clipList.length - 1].url !== prevClipList[prevClipList.length - 1].url
    ) {
      this.setPlayerToFirstClip();
    }
  };

  onSliderChange = (e: any, value: any) => {
    const { clipList, clipStartingPoints } = this.props;
    const { isSliderDragged } = this.state;
    const clipIndex = _.findLastIndex(clipStartingPoints, (point) => value >= point);
    const clipToMoveTo = clipList[clipIndex];
    const newCurrentTime = clipToMoveTo.markInTime + value - clipStartingPoints[clipIndex];

    if (!isSliderDragged) {
      this.setState({
        isSliderDragged: true,
      });
      this.player.pause();
      SetInterval.clear('updatePreviewPlayer');
    }

    this.navigateTo({
      clipIndex,
      sliderPosition: value,
      time: newCurrentTime,
    });
  };

  onSliderCommitted = () => {
    const { isPlaying } = this.state;

    this.setState({
      isSliderDragged: false,
    });

    if (isPlaying) {
      this.player.ready(() => {
        this.handlePlay();
      });
    }
  };

  navigateTo = ({ clipIndex, sliderPosition, time }: { clipIndex: number, sliderPosition: number, time: number }) => {
    const { clipList } = this.props;
    const { currentClipIndex } = this.state;
    const clipToMoveTo = clipList[clipIndex];

    if (clipToMoveTo.url !== clipList[currentClipIndex].url && typeof clipList[currentClipIndex].url === 'string') {
      const extension = clipToMoveTo.url.split('.').pop();
      this.player.src({
        src: clipToMoveTo.url,
        type: extension === 'm3u8' ? 'application/x-mpegURL' : 'video/mp4',
      });
    }
    this.player.ready(() => {
      this.player.currentTime(time);
    });
    this.setState({
      currentPosition: sliderPosition,
      currentClipIndex: clipIndex,
    });
  };

  handlePlay = () => {
    const { totalClipsDuration } = this.props;
    const { currentPosition } = this.state;

    if (currentPosition === totalClipsDuration) {
      this.setPlayerToFirstClip();
      this.player.ready(() => {
        this.play();
      });
    } else {
      this.play();
    }
  };

  play = async () => {
    const {
      clipList,
      clipStartingPoints,
      totalClipsDuration,
    } = this.props;

    let isLiveClip = false;
    if (this.player.currentSrc() && typeof this.player.currentSrc() === 'string') {
      isLiveClip = this.player.currentSrc().split('.').pop() === 'm3u8';
    }
    let streamClipStarttime = 0;

    if (isLiveClip) {
      streamClipStarttime = this.player.currentTime();
    }

    safePlay(this.player);

    this.setState({
      isPlaying: true,
    });

    SetInterval.start(
      () => {
        const { currentClipIndex } = this.state;
        const { markInTime, markOutTime } = clipList[currentClipIndex];

        let currentTime = this.player.currentTime();
        let isAtLiveEdge = false;
        if (this.player.currentSrc().split('.').pop() === 'm3u8') {
          isAtLiveEdge = this.player.player().liveTracker.atLiveEdge();

          if (streamClipStarttime !== 0 && currentTime !== streamClipStarttime) {
            this.player.currentTime(streamClipStarttime);
          }
        }
        currentTime = this.player.currentTime();
        if (currentTime >= markOutTime && streamClipStarttime === 0 && !isAtLiveEdge) {
          const nextClipIndex = currentClipIndex + 1;
          const nextClip = clipList[nextClipIndex];

          if (nextClip) {
            // switch to next clip
            if (this.player.src() !== nextClip.url && typeof nextClip.url === 'string') {
              this.player.pause();
              SetInterval.clear('updatePreviewPlayer');

              const extension = nextClip.url.split('.').pop();
              this.player.src({
                src: nextClip.url,
                type: extension === 'm3u8' ? 'application/x-mpegURL' : 'video/mp4',
              });
              this.player.ready(() => {
                this.player.currentTime(nextClip.markInTime);
                this.setState({
                  currentClipIndex: nextClipIndex,
                });
                const { isPlaying } = this.state;
                if (isPlaying) {
                  this.play();
                }
              });
            } else {
              this.player.currentTime(nextClip.markInTime);
              this.setState({
                currentClipIndex: nextClipIndex,
              });
            }
          } else {
            // finish preview
            this.player.currentTime(markOutTime);
            this.setState({
              currentPosition: totalClipsDuration,
            });
            this.pause();
          }
        } else {
          currentTime = this.player.currentTime();
          if (streamClipStarttime !== 0 && currentTime === streamClipStarttime && !isAtLiveEdge) {
            streamClipStarttime = 0;
          }
          // update slider position
          const newPosition = currentTime - markInTime + clipStartingPoints[currentClipIndex];
          this.setState({
            currentPosition: newPosition,
          });
        }
      },
      oneFrameInMs,
      'updatePreviewPlayer',
    );
  };

  pause = () => {
    SetInterval.clear('updatePreviewPlayer');
    this.player.pause();
    this.setState({
      isPlaying: false,
    });
  };

  assignThumbnail = () => {
    const { setThumbnailFrameConnect } = this.props;
    const { currentClipIndex } = this.state;

    setThumbnailFrameConnect({
      time: this.player.currentTime(),
      clipIndex: currentClipIndex,
    });
  };

  showThumbnail = () => {
    const {
      clipList,
      clipStartingPoints,
      thumbnailFrame: {
        time,
        clipIndex,
      },
    } = this.props;

    const sliderPosition = time - clipList[clipIndex].markInTime + clipStartingPoints[clipIndex];

    this.pause();
    this.navigateTo({
      clipIndex,
      sliderPosition,
      time,
    });
  };

  handleAddPreRoll = () => {
    const { openPrePostRollSelectionConnect } = this.props;

    this.pause();
    openPrePostRollSelectionConnect(ROLL.PRE);
  };

  handleAddPostRoll = () => {
    const { openPrePostRollSelectionConnect } = this.props;

    this.pause();
    openPrePostRollSelectionConnect(ROLL.POST);
  };

  render() {
    const {
      totalClipsDuration,
      marks,
      isPrePostRollLoading,
      thumbnailSelectable,
      thumbnailFrame,
    } = this.props;

    const {
      isReady,
      isPlaying,
      currentPosition,
    } = this.state;

    return (
      <Container>
        <PlayerContainer>
          <div
            id="PublishPreviewVideoContainer"
            data-vjs-player
            onContextMenu={(e) => {
              e.preventDefault();
            }}
          >
            <video
              id={clipsPreviewVideoJsOptions.id}
              className="video-js vjs-16-9 vjs-big-play-centered"
            />
          </div>
          <Controls>
            <AddPreRollButton
              id="PublishPreviewPlayerPreRoll"
              variant="plainOnPaper"
              onClick={this.handleAddPreRoll}
            >
              + Preroll
            </AddPreRollButton>
            <PlayPauseButton
              id="PublishPreviewPlayerPlay"
              variant="plainOnPaper"
              disabled={!isReady || isPrePostRollLoading}
              onClick={isPlaying ? this.pause : this.handlePlay}
            >
              {isPlaying ? 'Pause' : 'Play'}
            </PlayPauseButton>
            <SliderContainer
              thumbnailSelectable={thumbnailSelectable}
            >
              <Slider
                value={currentPosition}
                min={0}
                max={totalClipsDuration}
                marks={marks}
                step={oneFrameInSec}
                onChange={this.onSliderChange}
                onChangeCommitted={this.onSliderCommitted}
              />
            </SliderContainer>
            {
              thumbnailSelectable && (
                <ThumbnailSelection
                  thumbnailFrame={thumbnailFrame}
                  onAssign={this.assignThumbnail}
                  onCheck={this.showThumbnail}
                />
              )
            }
            <VaultButton
              id="PublishPreviewPlayerPreRoll"
              variant="plainOnPaper"
              onClick={this.handleAddPostRoll}
            >
              + Postroll
            </VaultButton>
          </Controls>
        </PlayerContainer>
        <ProgressContainer>
          {isPrePostRollLoading && <LinearProgress />}
        </ProgressContainer>
        <PrePostRollDialog />
      </Container>
    );
  }
}

const mapStateToProps = (state: IAppState): IStateProps => ({
  clipList: selectSelectedClipsWithPrePostRolls(state),
  clipStartingPoints: selectClipStartingPoints(state),
  totalClipsDuration: selectTotalClipsDuration(state),
  marks: selectMarksForPublishPreviewPlayer(state),
  isPrePostRollLoading: selectIsPrePostRollLoading(state),
  thumbnailFrame: selectThumbnailFrame(state),
  sourceUrl: selectSource(state),
});

const mapDispatchToProps: IDispatchProps = {
  openPrePostRollSelectionConnect: openPrePostRollSelection,
  setThumbnailFrameConnect: setThumbnailFrame,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ClipsPreviewPlayer);
